import i18next from 'i18next';
import { LOCALE_MAP } from './locale-formatting';

const currentLocale = () => LOCALE_MAP[i18next.language] ?? 'pt-BR';

export const formatDateToDateBR = (dateToFormat, locale = currentLocale()) => {
    if (!dateToFormat) return ''
    return new Intl.DateTimeFormat(locale, {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
    }).format(new Date(dateToFormat))
}

export const formatDateToDateBRPolish = (dateToFormat, locale = currentLocale()) => {
    if (!dateToFormat) return ''
    return new Intl.DateTimeFormat(locale, {
        day: '2-digit',
        month: 'long',
        year: 'numeric',
    }).format(new Date(dateToFormat))
}

export const formatDateTimeToDateTimeBR = (dateToFormat, locale = currentLocale()) => {
    if (!dateToFormat) return ''
    return new Intl.DateTimeFormat(locale, {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
    }).format(new Date(dateToFormat))
}

export const formatDateLongWithTime = (dateToFormat, locale = currentLocale()) => {
    if (!dateToFormat) return ''
    return new Intl.DateTimeFormat(locale, {
        day: '2-digit',
        month: 'long',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    }).format(new Date(dateToFormat))
}

// Fallback when a tenant has no timezone set, or it isn't known yet: the backend
// languageMiddleware's own DEFAULT_TIMEZONE (the platform's primary market), not UTC — a silent
// UTC fallback is what every post-tenant-switch Logs page used to render in, unlabeled.
export const DEFAULT_TIMEZONE = 'America/Sao_Paulo'

// The real UTC offset label for a timezone at a given instant, e.g. "GMT-3" (DST-correct).
export const formatTimezoneLabel = (companyTimezone, dateToFormat = new Date(), locale = currentLocale()) => {
    const tz = companyTimezone || DEFAULT_TIMEZONE
    const part = new Intl.DateTimeFormat(locale, { timeZone: tz, timeZoneName: 'shortOffset' })
        .formatToParts(new Date(dateToFormat))
        .find(p => p.type === 'timeZoneName')
    return part ? part.value : tz
}

export const formatDateTimeWithTimezone = (dateToFormat, companyTimezone, locale = currentLocale()) => {
    if (!dateToFormat) return ''
    const tz = companyTimezone || DEFAULT_TIMEZONE
    const formatted = new Intl.DateTimeFormat(locale, {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        timeZone: tz,
    }).format(new Date(dateToFormat))
    return `${formatted} (${formatTimezoneLabel(tz, dateToFormat, locale)})`
}

export const formatDateOnlyWithTimezone = (dateToFormat, companyTimezone, locale = currentLocale()) => {
    if (!dateToFormat) return ''
    const tz = companyTimezone || DEFAULT_TIMEZONE
    return new Intl.DateTimeFormat(locale, {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        timeZone: tz,
    }).format(new Date(dateToFormat))
}

// Always labeled with the real offset ("14:03:22 (GMT-3)"): a bare time is ambiguous the moment
// the viewer, the tenant and the server aren't all in the same zone.
export const formatTimeOnlyWithTimezone = (dateToFormat, companyTimezone, locale = currentLocale()) => {
    if (!dateToFormat) return ''
    const tz = companyTimezone || DEFAULT_TIMEZONE
    const formatted = new Intl.DateTimeFormat(locale, {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        timeZone: tz,
    }).format(new Date(dateToFormat))
    return `${formatted} (${formatTimezoneLabel(tz, dateToFormat, locale)})`
}
